import React, { useState, useCallback } from "react";
import {
  Container,
  Title,
  Group,
  Button,
  Table,
  Text,
  Stack,
  Card,
  Loader,
  Center,
  ActionIcon,
  Tooltip,
  Modal,
  Badge,
  Menu,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  IconArrowLeft,
  IconSend,
  IconTrash,
  IconFlask,
  IconDotsVertical,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { bridgeService, Device } from "../../../services/bridgeService";
import {
  DeviceStatusBadge,
  CommandDispatchModal,
} from "../../../components/bridge";
import { useBridgeStatus } from "../../../hooks/useBridgeStatus";
import { useFacilities } from "../../../hooks/useReferenceData";
import { StickerPrinterTestPrintButton } from "../../../components/sticker/StickerPrinterTestPrintButton";

dayjs.extend(relativeTime);

const BridgeWorkstationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [commandModalOpen, setCommandModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Device | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  useBridgeStatus();
  const { data: facilities = [] } = useFacilities();

  const { data: workstation, isLoading } = useQuery({
    queryKey: ["bridge-workstation", id],
    queryFn: () => bridgeService.getWorkstation(id!),
    select: (res) => res.data,
    enabled: !!id,
  });

  const deleteDeviceMutation = useMutation({
    mutationFn: (deviceId: string) => bridgeService.deleteDevice(id!, deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bridge-workstation", id] });
      queryClient.invalidateQueries({ queryKey: ["bridge-workstations"] });
      notifications.show({
        title: "Device Deleted",
        message: `"${deleteTarget?.name}" has been removed.`,
        color: "green",
      });
      setDeleteTarget(null);
    },
    onError: (err: any) => {
      notifications.show({
        title: "Delete Failed",
        message: err?.response?.data?.message || "Failed to delete device",
        color: "red",
      });
    },
  });

  const deleteWorkstationMutation = useMutation({
    mutationFn: () => bridgeService.deleteWorkstation(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bridge-workstations"] });
      notifications.show({
        title: "Workstation Deleted",
        message: `"${workstation?.name}" has been removed.`,
        color: "green",
      });
      navigate("/admin/bridge");
    },
    onError: (err: any) => {
      notifications.show({
        title: "Delete Failed",
        message: err?.response?.data?.message || "Failed to delete workstation",
        color: "red",
      });
    },
  });

  const stainerMutation = useMutation({
    mutationFn: (enabled: boolean) =>
      bridgeService.setStainerBridge(id!, enabled),
    onSuccess: (_res, enabled) => {
      queryClient.invalidateQueries({ queryKey: ["bridge-workstation", id] });
      queryClient.invalidateQueries({ queryKey: ["bridge-workstations"] });
      notifications.show({
        title: enabled ? "Staining Hub Set" : "Staining Hub Cleared",
        message: enabled
          ? `"${workstation?.name}" now handles all staining.`
          : `"${workstation?.name}" is no longer the Staining Hub.`,
        color: "green",
      });
    },
    onError: (err: any) => {
      notifications.show({
        title: "Update Failed",
        message:
          err?.response?.data?.message || "Failed to update Staining Hub",
        color: "red",
      });
    },
  });

  const handleBack = useCallback(() => {
    navigate("/admin/bridge");
  }, [navigate]);

  const handleOpenCommand = useCallback(() => {
    setCommandModalOpen(true);
  }, []);

  const handleCloseCommand = useCallback(() => {
    setCommandModalOpen(false);
  }, []);

  const handleToggleStainer = useCallback(() => {
    stainerMutation.mutate(!workstation?.isStainerBridge);
  }, [stainerMutation, workstation?.isStainerBridge]);

  const handleOpenDeleteWorkstation = useCallback(() => {
    setConfirmDeleteOpen(true);
  }, []);

  const handleCloseDeleteWorkstation = useCallback(() => {
    setConfirmDeleteOpen(false);
  }, []);

  const handleConfirmDeleteWorkstation = useCallback(() => {
    deleteWorkstationMutation.mutate();
  }, [deleteWorkstationMutation]);

  const handleCloseDeviceDelete = useCallback(() => {
    setDeleteTarget(null);
  }, []);

  const handleConfirmDeleteDevice = useCallback(() => {
    if (deleteTarget) {
      deleteDeviceMutation.mutate(deleteTarget.id);
    }
  }, [deleteTarget, deleteDeviceMutation]);

  if (isLoading)
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  if (!workstation) return <Text>Workstation not found</Text>;

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="lg">
        <Group>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={handleBack}
          >
            Back
          </Button>
          <Title order={2}>{workstation.name}</Title>
          <DeviceStatusBadge status={workstation.status} />
          {workstation.isStainerBridge && (
            <Badge
              color="teal"
              variant="light"
              leftSection={<IconFlask size={12} />}
            >
              Staining Hub
            </Badge>
          )}
        </Group>
        <Menu position="bottom-end" offset={4} withinPortal>
          <Menu.Target>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="lg"
              aria-label="Workstation actions"
            >
              <IconDotsVertical size={18} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconFlask size={16} />}
              disabled={stainerMutation.isPending}
              onClick={handleToggleStainer}
            >
              {workstation.isStainerBridge
                ? "Unset as Staining Hub"
                : "Set as Staining Hub"}
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={handleOpenDeleteWorkstation}
            >
              Delete workstation
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>

      <Card shadow="sm" padding="lg" radius="md" withBorder mb="lg">
        <Stack gap="xs">
          <Text size="sm">Status: {workstation.status}</Text>
          {workstation.facilityId && (
            <Text size="sm">
              Facility:{" "}
              {facilities.find((f) => f.id === workstation.facilityId)?.name ||
                workstation.facilityId}
            </Text>
          )}
          <Text size="sm">
            Local Bridge Port: {workstation.localPort || 8765}
          </Text>
          <Text size="sm">
            IP Address:{" "}
            {workstation.currentIpAddresses &&
            workstation.currentIpAddresses.length > 0
              ? workstation.currentIpAddresses.join(", ")
              : "Unknown"}
          </Text>
          <Text size="sm">
            Last Seen:{" "}
            {workstation.lastSeenAt
              ? dayjs(workstation.lastSeenAt).format("YYYY-MM-DD HH:mm:ss")
              : "Never"}
          </Text>
          {workstation.activeSession && (
            <Text size="sm">
              Session: Connected{" "}
              {dayjs(workstation.activeSession.connectedAt).fromNow()}
            </Text>
          )}
        </Stack>
      </Card>

      <Group justify="space-between" mb="md">
        <Title order={3}>Devices</Title>
        <Button
          leftSection={<IconSend size={16} />}
          onClick={handleOpenCommand}
        >
          Send Command
        </Button>
      </Group>

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th>Connection</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Last Seen</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {(workstation.devices || []).map((device) => (
            <Table.Tr key={device.id}>
              <Table.Td>{device.name}</Table.Td>
              <Table.Td>{device.deviceType}</Table.Td>
              <Table.Td>
                {device.connectionType}: {device.connectionAddress}
              </Table.Td>
              <Table.Td>
                <DeviceStatusBadge status={device.status} />
              </Table.Td>
              <Table.Td>
                {device.lastSeenAt
                  ? dayjs(device.lastSeenAt).fromNow()
                  : "Never"}
              </Table.Td>
              <Table.Td>
                <Group gap="xs">
                  {device.deviceType === "sticker_printer" && (
                    <StickerPrinterTestPrintButton
                      deviceId={device.id}
                      workstationId={workstation.id}
                    />
                  )}
                  <Tooltip label="Delete device">
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      onClick={() => setDeleteTarget(device)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <CommandDispatchModal
        opened={commandModalOpen}
        onClose={handleCloseCommand}
        devices={workstation.devices || []}
      />

      <Modal
        opened={!!deleteTarget}
        onClose={handleCloseDeviceDelete}
        title="Confirm Delete Device"
        size="sm"
      >
        <Stack>
          <Text size="sm">
            Are you sure you want to delete device &quot;{deleteTarget?.name}
            &quot;? This action cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={handleCloseDeviceDelete}>
              Cancel
            </Button>
            <Button
              color="red"
              loading={deleteDeviceMutation.isPending}
              onClick={handleConfirmDeleteDevice}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={confirmDeleteOpen}
        onClose={handleCloseDeleteWorkstation}
        title="Confirm Delete Workstation"
        size="sm"
      >
        <Stack>
          <Text size="sm">
            Are you sure you want to delete workstation &quot;{workstation.name}
            &quot;? This will also delete all associated devices, sessions, and
            commands. This action cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={handleCloseDeleteWorkstation}>
              Cancel
            </Button>
            <Button
              color="red"
              loading={deleteWorkstationMutation.isPending}
              onClick={handleConfirmDeleteWorkstation}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};

export default BridgeWorkstationDetail;
